/**
 * Sage Chat — Shared file type constants
 *
 * Single source of truth for all Sage file upload surfaces
 * (SmartChat widget, /ask-sage page, Add to Case button).
 */

// MIME types Sage accepts
export const SAGE_ACCEPTED_MIME_TYPES = [
  // Images (sent as base64 to Claude Vision)
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  // PDF (converted to page images + OCR text)
  'application/pdf',
  // Word documents (text extracted via mammoth)
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // Spreadsheets (parsed to structured text via SheetJS)
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  // Plain text (read directly)
  'text/plain',
  'text/rtf',
  'application/rtf',
] as const;

/** Comma-joined string for <input accept="..."> */
export const SAGE_ACCEPT_FILTER = SAGE_ACCEPTED_MIME_TYPES.join(',');

/** Also accept by extension for browsers that don't match MIME reliably */
export const SAGE_ACCEPT_EXTENSIONS =
  '.png,.jpg,.jpeg,.webp,.gif,.pdf,.doc,.docx,.xlsx,.xls,.csv,.txt,.rtf';

/** Combined accept string (MIME types + extensions) */
export const SAGE_ACCEPT = `${SAGE_ACCEPT_FILTER},${SAGE_ACCEPT_EXTENSIONS}`;

export const SAGE_MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
export const SAGE_MAX_ATTACHMENTS = 25;
export const SAGE_MAX_TEXT_CHARS = 100_000;

// ---------------------------------------------------------------------------
// File type detection helpers
// ---------------------------------------------------------------------------

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

export function isImageFile(file: File): boolean {
  return IMAGE_TYPES.has(file.type);
}

export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

export function isWordFile(file: File): boolean {
  const t = file.type;
  const n = file.name.toLowerCase();
  return (
    t === 'application/msword' ||
    t === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    n.endsWith('.doc') ||
    n.endsWith('.docx')
  );
}

export function isSpreadsheetFile(file: File): boolean {
  const t = file.type;
  const n = file.name.toLowerCase();
  return (
    t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    t === 'application/vnd.ms-excel' ||
    t === 'text/csv' ||
    n.endsWith('.xlsx') ||
    n.endsWith('.xls') ||
    n.endsWith('.csv')
  );
}

export function isTextFile(file: File): boolean {
  const t = file.type;
  const n = file.name.toLowerCase();
  return (
    t === 'text/plain' ||
    t === 'text/rtf' ||
    t === 'application/rtf' ||
    n.endsWith('.txt') ||
    n.endsWith('.rtf')
  );
}
